import Flex from 'quarks/Flex';
import Heading from 'quarks/Heading';
import Image, { ImageData } from 'quarks/Image';
import Link from 'quarks/Link';
import Text from 'quarks/Text';

import Breadcrumbs from 'components/Breadcrumbs/Breadcrumbs';
import ComponentButton from 'components/Button/ComponentButton';
import AllCaseStudyItems from 'components/CaseStudy/AllCaseStudyItems';

import richTextParser from 'utils/richTextParser';

import type { ContentfulComponentSingleInstance } from 'graphqlTypes';
import type { FC } from 'react';

type CaseListingHeroProps = ContentfulComponentSingleInstance & {
  backgroundIsDark?: boolean;
};

const CaseListingHero: FC<CaseListingHeroProps> = ({ heading, body, backgroundIsDark }) => {
  const posts = AllCaseStudyItems();
  const breadcrumbs = [
    { label: 'Resources', link: '/resources' },
    { label: 'Case Studies', link: '/resources/case-studies' },
  ];

  return (
    <Flex flexDirection="column">
      <Flex flexDirection="column">
        <Flex
          position="relative"
          flexDirection="column"
          width="100%"
          maxWidth="100%"
          marginX="auto"
          gap="40px"
          zIndex="1"
          md={{ maxWidth: '100%', gap: '64px' }}
        >
          <Flex marginBottom={24} md={{ marginBottom: 32 }}>
            <Breadcrumbs breadcrumbs={breadcrumbs} isDark={true} />
          </Flex>
          <Flex
            flexDirection="column"
            justifyContent="space-between"
            alignItems="flex-end"
            width="100%"
            gap="32px"
            xl={{ flexDirection: 'row' }}
          >
            <Flex flexDirection="column" gap="24px" width="100%" maxWidth="700px">
              {heading && (
                <Heading
                  fontSize="h3"
                  textColor="common-white"
                  fontFamily="secondaryFont"
                  sm={{ fontSize: 'h2', lineHeight: 'h2' }}
                  lg={{ fontSize: 'h1', lineHeight: 'h1' }}
                >
                  {heading}
                </Heading>
              )}
              {body && (
                <Flex
                  textColor="common-white"
                  fontSize="textMd"
                  sm={{ fontSize: 'textMd' }}
                  lg={{ fontSize: 'textLg' }}
                >
                  {richTextParser(body)}
                </Flex>
              )}
            </Flex>
          </Flex>
          {posts[0] && (
            <Link href={`/resources/case-studies/${posts[0]?.slug}`} textDecoration="none">
              <Flex
                flexDirection="column"
                justifyContent="space-between"
                borderRadius="16px"
                overflowX="hidden"
                overflowY="hidden"
                backgroundColor="common-white"
                boxShadow="xl"
                height="100%"
                maxHeight="max-content"
                lg={{ flexDirection: 'row', maxHeight: '360px' }}
              >
                {posts[0]?.seo?.openGraphImage && (
                  <Flex width="100%" height="100%" lg={{ width: '50%' }} xl={{ width: '830px' }}>
                    <Image
                      image={posts[0]?.seo?.openGraphImage as ImageData}
                      alt={posts[0]?.title || ''}
                      width="100%"
                      height="100%"
                      objectFit="cover"
                      css={{
                        backdropFilter: backgroundIsDark ? 'blur(10px)' : 'blur(15px)',
                      }}
                    />
                  </Flex>
                )}
                <Flex
                  flexDirection="column"
                  justifyContent="space-between"
                  width="100%"
                  maxWidth="100%"
                  gap="16px"
                  paddingAll={24}
                  lg={{ maxWidth: '50%', paddingY: 32, paddingX: 40 }}
                  xl={{ maxWidth: '450px' }}
                  css={`
                    button {
                      padding: 0;

                      @media (min-width: 991px) {
                        position: absolute;
                        bottom: 20px;
                      }
                    }
                  `}
                >
                  {posts[0]?.category && (
                    <Flex backgroundColor="blue-50" width="fit-content" paddingY={4} paddingX={12} borderRadius="16px">
                      <Text textStyle="textXs" fontWeight="semiBold" textColor="blue-900">
                        {posts[0].category}
                      </Text>
                    </Flex>
                  )}
                  {posts[0]?.title && (
                    <Heading as="h4" textStyle="h4" textColor="gray-900" md={{ textStyle: 'h3' }}>
                      {posts[0].title}
                    </Heading>
                  )}
                  <ComponentButton
                    link={`/resources/case-studies/${posts[0]?.slug}`}
                    label="Read More"
                    endIcon="Arrow Right"
                    buttonColor="Gray 900 Link"
                  />
                </Flex>
              </Flex>
            </Link>
          )}
        </Flex>
        <Flex
          width="100vw"
          height="100%"
          maxHeight="700px"
          position="absolute"
          backgroundColor="navy-900"
          top="0"
          left="0"
          zIndex="-1"
          xs={{ top: '-7rem' }}
          md={{ maxHeight: '800px', top: '0' }}
          lg={{ maxHeight: '600px' }}
        />
      </Flex>
    </Flex>
  );
};

export default CaseListingHero;
